import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Crown, Users, TrendingUp, DollarSign, Calendar,
  Download, Search, RefreshCw, Mail, Shield,
  UserCheck, UserX, Activity, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const AdminPanel = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [newPlan, setNewPlan] = useState('');
  const [broadcasting, setBroadcasting] = useState(false);

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, eventsRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/users'),
        api.get('/admin/events?limit=50')
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      toast.error('Error cargando datos de administración');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePlan = async () => {
    if (!selectedUser || !newPlan) return;

    try {
      await api.put(`/admin/users/${selectedUser.id}/plan`, { plan: newPlan });
      toast.success(`Plan actualizado a ${newPlan}`);
      setShowUserDialog(false);
      loadAdminData();
    } catch (error) {
      toast.error('Error actualizando plan');
    }
  };

  const handleBroadcastUpdate = async () => {
    if (!confirm(`¿Enviar email de actualización a TODOS los usuarios verificados? Esta acción no se puede deshacer.`)) return;
    setBroadcasting(true);
    try {
      const res = await api.post('/admin/broadcast-email');
      toast.success(res.data.message);
    } catch (error) {
      toast.error('Error al enviar emails');
    } finally {
      setBroadcasting(false);
    }
  };

  const handleExportEvents = async () => {
    try {
      const response = await api.get('/admin/events/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `trebol_events_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Eventos exportados');
    } catch (error) {
      toast.error('Error exportando eventos');
    }
  };

  const filteredUsers = users.filter(u =>
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-amber-500/10 rounded-sm flex items-center justify-center">
            <Crown className="w-6 h-6 text-amber-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Panel CEO</h1>
            <p className="text-sm text-muted-foreground">Administración de Trebol Finance by Leapifyes</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleBroadcastUpdate}
            disabled={broadcasting}
            className="rounded-sm bg-emerald-700 hover:bg-emerald-600 text-white text-xs"
          >
            <Mail size={16} className="mr-2" />
            {broadcasting ? 'Enviando...' : 'Email actualización'}
          </Button>
          <Button onClick={loadAdminData} variant="outline" className="rounded-sm">
            <RefreshCw size={16} className="mr-2" />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-border rounded-sm shadow-none">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Total Usuarios</p>
                <p className="text-2xl font-bold text-foreground">{stats?.total_users || 0}</p>
              </div>
              <Users className="w-8 h-8 text-primary/50" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border rounded-sm shadow-none">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Premium</p>
                <p className="text-2xl font-bold text-foreground">{stats?.premium_users || 0}</p>
              </div>
              <Shield className="w-8 h-8 text-amber-500/50" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border rounded-sm shadow-none">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Nuevos (7d)</p>
                <p className="text-2xl font-bold text-foreground">{stats?.new_users_7d || 0}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-emerald-500/50" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border rounded-sm shadow-none">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Eventos (24h)</p>
                <p className="text-2xl font-bold text-foreground">{stats?.events_24h || 0}</p>
              </div>
              <Activity className="w-8 h-8 text-blue-500/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card className="border-border rounded-sm shadow-none">
        <CardHeader className="border-b border-border">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Users size={18} className="text-primary" />
              Gestión de Usuarios
            </CardTitle>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar usuario..."
                  className="pl-9 w-64 rounded-sm"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">Usuario</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">Plan</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">Registro</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">Onboarding</th>
                  <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredUsers.map((u) => (
                  <tr key={u.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-foreground text-sm">{u.name}</p>
                        <p className="text-xs text-muted-foreground">{u.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={cn(
                        "text-xs px-2 py-1 rounded-full font-medium",
                        u.plan === 'premium' ? "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400" :
                          u.plan === 'free' ? "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400" :
                            "bg-primary/10 text-primary"
                      )}>
                        {u.plan || 'free'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {formatDate(u.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      {u.onboarding_completed ? (
                        <UserCheck size={18} className="text-emerald-500" />
                      ) : (
                        <UserX size={18} className="text-muted-foreground" />
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedUser(u);
                          setNewPlan(u.plan || 'free');
                          setShowUserDialog(true);
                        }}
                        className="text-primary hover:text-primary hover:bg-primary/10"
                      >
                        Gestionar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Events Log */}
      <Card className="border-border rounded-sm shadow-none">
        <CardHeader className="border-b border-border">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Activity size={18} className="text-primary" />
              Registro de Eventos
            </CardTitle>
            <Button onClick={handleExportEvents} variant="outline" size="sm" className="rounded-sm">
              <Download size={14} className="mr-2" />
              Exportar CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0 max-h-96 overflow-y-auto">
          <div className="divide-y divide-border">
            {events.slice(0, 20).map((event, idx) => (
              <div key={idx} className="px-4 py-3 flex items-center justify-between hover:bg-muted/30">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "w-2 h-2 rounded-full",
                    event.event_type?.includes('error') ? "bg-red-500" :
                      event.event_type?.includes('login') ? "bg-emerald-500" :
                        event.event_type?.includes('register') ? "bg-blue-500" :
                          "bg-primary"
                  )} />
                  <div>
                    <p className="text-sm font-medium text-foreground">{event.event_type}</p>
                    <p className="text-xs text-muted-foreground">{event.user_email || 'Sistema'}</p>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground">
                  {formatDate(event.timestamp)}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* User Management Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle>Gestionar Usuario</DialogTitle>
          </DialogHeader>

          {selectedUser && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-muted/50 rounded-sm">
                <p className="font-medium text-foreground">{selectedUser.name}</p>
                <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
              </div>

              <div className="space-y-2">
                <Label>Plan de Usuario</Label>
                <Select value={newPlan} onValueChange={setNewPlan}>
                  <SelectTrigger className="rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="love">Love (Pareja)</SelectItem>
                    <SelectItem value="team">Team</SelectItem>
                    <SelectItem value="family">Family</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserDialog(false)} className="rounded-sm">
              Cancelar
            </Button>
            <Button onClick={handleUpdatePlan} className="bg-primary hover:bg-primary/90 rounded-sm">
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
